import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { useAppContext } from "./useAppContext";
import { getAccessToken } from "@/shared/types/auth";

import type { FoldersResponse, AutoGroupResponse, FolderCreateResponse, FolderDeleteResponse } from "@/shared/types/folderTypes";

function buildUrl(estateId: number, path: string): string {
  return `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/documents/${estateId}${path}`;
}

async function authHeaders(): Promise<Record<string, string>> {
  const accessToken = await getAccessToken();
  if (!accessToken) throw new Error("Authentication required");
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`,
    apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
  };
}

async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  const headers = await authHeaders();
  const response = await fetch(url, { ...options, headers });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error((data as { error?: string }).error || `Request failed: ${response.status}`);
  }
  return response;
}

export function useFolders() {
  const { person } = useAppContext();
  const estateId = person?.estate_id;

  return useQuery({
    queryKey: ["document-folders", estateId],
    queryFn: async (): Promise<FoldersResponse> => {
      if (!estateId) throw new Error("Estate ID required");
      const response = await fetchWithAuth(buildUrl(estateId, "/folders"));
      return response.json();
    },
    enabled: !!estateId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

export function useCreateFolder() {
  const { person } = useAppContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { name: string }): Promise<FolderCreateResponse> => {
      if (!person?.estate_id) throw new Error("Estate ID required");
      const response = await fetchWithAuth(buildUrl(person.estate_id, "/folder"), {
        method: "POST",
        body: JSON.stringify(params),
      });
      return response.json() as Promise<FolderCreateResponse>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["document-folders", person?.estate_id] });
      queryClient.invalidateQueries({ queryKey: ["documents", person?.estate_id] });
    },
  });
}

export function useRenameFolder() {
  const { person } = useAppContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ folderId, name }: { folderId: number; name: string }) => {
      if (!person?.estate_id) throw new Error("Estate ID required");
      const response = await fetchWithAuth(buildUrl(person.estate_id, `/folder/${folderId}`), {
        method: "PUT",
        body: JSON.stringify({ name }),
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["document-folders", person?.estate_id] });
    },
  });
}

export function useDeleteFolder() {
  const { person } = useAppContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (folderId: number): Promise<FolderDeleteResponse> => {
      if (!person?.estate_id) throw new Error("Estate ID required");
      const response = await fetchWithAuth(buildUrl(person.estate_id, `/folder/${folderId}`), {
        method: "DELETE",
      });
      return response.json() as Promise<FolderDeleteResponse>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["document-folders", person?.estate_id] });
      queryClient.invalidateQueries({ queryKey: ["documents", person?.estate_id] });
    },
  });
}

export function useMoveDocument() {
  const { person } = useAppContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ documentId, folderId }: { documentId: number; folderId: number | null }) => {
      if (!person?.estate_id) throw new Error("Estate ID required");
      const response = await fetchWithAuth(
        buildUrl(person.estate_id, `/document/${documentId}/move`),
        {
          method: "PUT",
          body: JSON.stringify({ folder_id: folderId }),
        },
      );
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["document-folders", person?.estate_id] });
      queryClient.invalidateQueries({ queryKey: ["documents", person?.estate_id] });
    },
  });
}

export function useAutoGroupFolders() {
  const { person } = useAppContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<AutoGroupResponse> => {
      if (!person?.estate_id) throw new Error("Estate ID required");
      const response = await fetchWithAuth(buildUrl(person.estate_id, "/folders/auto-group"), {
        method: "POST",
        body: JSON.stringify({}),
      });
      return response.json() as Promise<AutoGroupResponse>;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ["document-folders", person?.estate_id] }),
        queryClient.refetchQueries({ queryKey: ["documents", person?.estate_id] }),
      ]);
      queryClient.invalidateQueries({ queryKey: ["documents", person?.estate_id] });
    },
  });
}
